import React, { useEffect, useState } from "react";
import Plot from "react-plotly.js";
import {
  Box,
  Paper,
  Typography,
  Divider,
  MenuItem,
  Select,
  Grid,
  FormControl,
  InputLabel,
} from "@mui/material";

const dayLabels = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function generateCalendarMatrix(impressionsMap, year, month) {
  const totalDays = new Date(year, month, 0).getDate();

  const matrix = [];
  const texts = [];
  const hovers = [];

  let week = new Array(7).fill(null);
  let weekText = new Array(7).fill("");
  let weekHover = new Array(7).fill("");

  for (let day = 1; day <= totalDays; day++) {
    const date = new Date(year, month - 1, day);
    const dayOfWeek = date.getDay();

    const impressions = impressionsMap[day] ? impressionsMap[day] / 1000 : 0; // Convert to thousands
    week[dayOfWeek] = impressions;
    weekText[dayOfWeek] = `${day}`;
    weekHover[dayOfWeek] = `Date: ${month}/${day}\nImpressions: ${impressions.toFixed(1)}k`;

    if (dayOfWeek === 6 || day === totalDays) {
      matrix.push([...week]);
      texts.push([...weekText]);
      hovers.push([...weekHover]);
      week = new Array(7).fill(null);
      weekText = new Array(7).fill("");
      weekHover = new Array(7).fill("");
    }
  }

  return { z: matrix, text: texts, hover: hovers };
}

const monthOptions = Array.from({ length: 12 }, (_, i) => ({
  label: new Date(0, i).toLocaleString("default", { month: "long" }),
  value: i + 1,
}));

const CampaignImpressionMonthly = ({ data = {}, selectedMonth, onMonthChange }) => {
  const currentYear = new Date().getFullYear();
  const [month, setMonth] = useState(selectedMonth);

  useEffect(() => {
    setMonth(selectedMonth);
  }, [selectedMonth]);

  const handleMonthChange = (e) => {
    const newMonth = e.target.value;
    setMonth(newMonth);
    if (onMonthChange) {
      onMonthChange(newMonth);
    }
  };

  const hasData = Object.keys(data).length > 0;
  console.log(data);
  return (
    <Box mt={3}>
      <Paper 
        elevation={3}
        sx={{ 
          p: 2, 
          mb: 3, 
          backgroundColor: "#fff",
          borderRadius: 2
        }}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md="auto">
            <Typography variant="h6" component="div" sx={{ mb: { xs: 2, md: 0 } }}>
              Monthly Impression Analysis
            </Typography>
          </Grid>
          <Grid item xs={12} md="auto">
            <FormControl fullWidth size="medium">
              <InputLabel>Month</InputLabel>
              <Select value={month} label="Month" onChange={handleMonthChange}>
                {monthOptions.map((m) => (
                  <MenuItem key={m.value} value={m.value}>
                    {m.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {!hasData ? (
          <Box 
            sx={{ 
              p: 4, 
              textAlign: 'center',
              minHeight: 200,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
              gap: 2
            }}
          >
            <Typography variant="h6" color="textSecondary">
              No impression data available for {monthOptions[month - 1].label}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Please select a different month or verify that data has been collected for this period.
            </Typography>
          </Box>
        ) : null}
      </Paper>

      {hasData && (
        Object.entries(data).map(([billboardId, billboardData]) => {
          const { daily_data = {} } = billboardData;
          const impressions_by_day = Object.entries(daily_data).reduce(
            (acc, [day, impressions]) => {
              acc[day] = impressions.reduce((sum, val) => sum + val, 0);
              return acc;
            },
            {}
          );

          const { z, text, hover } = generateCalendarMatrix(
            impressions_by_day,
            currentYear,
            month
          );

          return (
            <Paper
              key={billboardId}
              elevation={4}
              sx={{ 
                p: 3, 
                mb: 4, 
                borderRadius: 3, 
                backgroundColor: "#f9f9f9",
                boxShadow: 3,
                maxWidth: "100%",
                minWidth: "900px",
                margin: "auto",
              }}
            >
              <Typography variant="h6"
                sx={{ mb: 2, color: "primary.main", fontWeight: 600 }}
              >
                Billboard: {billboardId} - {billboardData.location} ({billboardData.height} x {billboardData.width})
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Box sx={{ width: "100%" }}>
                <Plot
                  data={[
                    {
                      z,
                      x: dayLabels,
                      y: Array.from({ length: z.length }, (_, i) => `Week ${i + 1}`),
                      text,
                      customdata: hover,
                      type: "heatmap",
                      hovertemplate: "%{customdata}<extra></extra>",
                      texttemplate: "%{text}",
                      colorscale: "Oranges",
                      showscale: true,
                      colorbar: {
                        title: "Impressions (thousands)",
                        titleside: "right"
                      }
                    }
                  ]}
                  layout={{
                    height: 400,
                    margin: { t: 30, l: 60, r: 80, b: 30 },
                    paper_bgcolor: "#fff",
                    plot_bgcolor: "#fff",
                  }}
                  useResizeHandler
                  style={{ width: "100%", height: "100%" }}
                  config={{ responsive: true, displayModeBar: true, displaylogo: false }}
                />
              </Box>
            </Paper>
          );
        })
      )}
    </Box>
  );
};

export default CampaignImpressionMonthly;